import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["tabs"]
  
  isFirstPopState = true

  connect() {
    console.log("BaseViewController connected")
    
    // Set initial tab based on hash or default to overview
    const tabId = window.location.hash.slice(1) || 'overview'
    const tab = this.tabsTarget.querySelector(`[data-id="${tabId}"]`)
    
    if (tab) {
      // Remove active class from all tabs
      Array.from(this.tabsTarget.children).forEach(child => {
        child.classList.remove("tab-active")
      })
      
      // Add active class to current tab
      tab.classList.add("tab-active")
      
      // Load the content with preserved parameters
      const url = this.buildUrlWithParams(tab.getAttribute('data-base-view-url-param'))
      if (url) {
        Turbo.visit(url, { frame: "data" })
      }
    }

    // Listen for back/forward navigation
    window.addEventListener('popstate', this.handlePopState.bind(this))
    
    // Store initial state
    if (!history.state?.tabId) {
      history.replaceState({ tabId: tabId }, '', `${location.pathname}${location.search}${location.hash}`)
    }
  }

  disconnect() {
    window.removeEventListener('popstate', this.handlePopState.bind(this))
  }

  // Helper method to build URL with preserved parameters
  buildUrlWithParams(baseUrl) {
    if (!baseUrl) return null
    
    const currentUrl = new URL(window.location.href)
    const newUrl = new URL(baseUrl, window.location.origin)
    
    // Preserve specific parameters like shared_note
    const paramsToPreserve = ['shared_note', 'highlight', 'filter']
    
    paramsToPreserve.forEach(param => {
      if (currentUrl.searchParams.has(param)) {
        newUrl.searchParams.set(param, currentUrl.searchParams.get(param))
      }
    })
    
    return newUrl.toString()
  }

  handlePopState = (event) => {
    // Get tab ID from state or hash or default to overview
    const tabId = event.state?.tabId || window.location.hash.slice(1) || 'overview'
    
    const tab = this.tabsTarget.querySelector(`[data-id="${tabId}"]`)
    
    if (tab) {
      // Remove active class from all tabs
      Array.from(this.tabsTarget.children).forEach(child => {
        child.classList.remove("tab-active")
      })
      
      // Add active class to current tab
      tab.classList.add("tab-active")
      
      // Load the content with preserved parameters
      const url = this.buildUrlWithParams(tab.getAttribute('data-base-view-url-param'))
      if (url) {
        // Get the URL parts before any hash
        const urlBeforeHash = url.split('#')[0]
        const currentUrlBeforeHash = window.location.href.split('#')[0]
        
        // If navigating to a different base URL or no hash present, do a full reload
        if (urlBeforeHash !== currentUrlBeforeHash || !window.location.hash) {
          location.reload()
        } else {
          Turbo.visit(url, { frame: "data" })
        }
      }

      // Ensure hash is correct
      if (window.location.hash !== `#${tabId}`) {
        history.replaceState({ tabId }, '', `${location.pathname}${location.search}#${tabId}`)
      }
    }
  }

  toggle(event) {
    event.preventDefault()
    
    // Get the element that was clicked (handle both target and currentTarget)
    const element = event.target.hasAttribute('role') ? event.target : event.currentTarget
    
    // Remove active class from all tabs
    Array.from(this.tabsTarget.children).forEach(child => {
      child.classList.remove("tab-active")
    })
    
    // Add active class to clicked tab
    element.classList.add("tab-active")
    
    // Get the URL from the data attribute with preserved parameters
    const url = this.buildUrlWithParams(element.getAttribute('data-base-view-url-param'))
    if (url) {
      Turbo.visit(url, { frame: "data" })
    }
    
    // Update URL hash and store state (preserve search params)
    const tabId = element.getAttribute('data-id')
    history.pushState({ tabId }, '', `${location.pathname}${location.search}#${tabId}`)
  }
}
